/**
 * About Page — Legacy Triangle
 * Company story, mission, team
 */

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Target, Users, Award, Shield, Heart, TrendingUp,
  ArrowRight, CheckCircle
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3Mzc5OTUzMHww&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: Heart, title: "Client-First", description: "Your success is our success. We go above and beyond." },
  { icon: Shield, title: "Transparency", description: "No hidden fees, no surprises. Honest advice always." },
  { icon: TrendingUp, title: "Results-Driven", description: "We measure our success by your funding outcomes." },
  { icon: Users, title: "Partnership", description: "We're with you for the long haul, not just one deal." },
];

const team = [
  { name: "Cisco Montes", role: "Founder & CEO", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/yz9ms14y_ChatGPT%20Image%20Mar%2017%2C%202026%2C%2004_19_28%20PM.png", color: "#48d480", position: "30%" },
  { name: "Brandon Thomas", role: "VP of Operations", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/vh49nbov_Untitled%20design%20%2833%29.png", color: "#e8821c", position: "top" },
  { name: "Johanna Gil", role: "Sales Manager", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/cnyegefw_WhatsApp%20Image%202024-12-02%20at%2022.29.47_17bf48f0.jpg", color: "#48d480", position: "30%" },
  { name: "Craig Gibbs", role: "Sr. Finance Manager", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/wq56u446_Gemini_Generated_Image_mqwb7ymqwb7ymqwb.png", color: "#e8821c", position: "top" },
  { name: "Love Garcia", role: "Advising Manager", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/3hl7t7wq_unnamed.jpg", color: "#48d480", position: "top" },
  { name: "Brett Bonnigan", role: "Marketing & Development", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/th6j3yf8_Gemini_Generated_Image_yd1i9wyd1i9wyd1i.png", color: "#e8821c", position: "top" },
  { name: "Amy Lee", role: "Credit Support Specialist", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/2vk1tvj8_Gemini_Generated_Image_bplwb9bplwb9bplw.png", color: "#48d480", position: "top" },
  { name: "Alex Cruz", role: "Customer Success Manager", image: "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/2eu3cn7k_ChatGPT%20Image%20Jun%2019%2C%202025%2C%2001_29_17%20PM.png", color: "#e8821c", position: "top" },
];

const achievements = [
  { value: "$50M+", label: "Funding Distributed" },
  { value: "10,000+", label: "Businesses Funded" },
  { value: "10+", label: "Years Experience" },
  { value: "100+", label: "Lending Partners" },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[85vh] md:min-h-[70vh] flex items-center overflow-hidden pt-24 md:pt-40">
        {/* Background image - positioned to show faces on all devices */}
        <div
          className="absolute inset-0"
          style={{ 
            backgroundImage: `url(${HERO_IMG})`, 
            backgroundSize: 'cover',
            backgroundPosition: 'top center'
          }}
        />
        {/* Overlay - stronger on mobile for readability, gradient on desktop */}
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-[#0f2d52]/95 via-[#0f2d52]/85 to-[#0f2d52]/70 md:to-transparent" />
        <div className="container relative z-10 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">About Us</span>
            </div>
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-4xl md:text-6xl font-black text-white leading-tight mb-6"
            >
              Empowering Businesses to
              <br />
              <span className="text-[#48d480]">Start, Fund & Grow</span>
            </h1>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/80 text-lg leading-relaxed mb-8 max-w-xl"
            >
              Legacy Triangle was founded with a simple mission: to make business funding accessible, transparent, and fast for entrepreneurs everywhere.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary flex items-center gap-2">
                Get in Touch <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="fade-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="section-label">Our Story</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-6"
              >
                Built by Entrepreneurs, for Entrepreneurs
              </h2>
              <div className="space-y-4 text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                <p className="leading-relaxed">
                  After experiencing firsthand the frustrations of securing business funding — the endless paperwork, confusing terms, and impersonal bank interactions — our founder set out to create a better way.
                </p>
                <p className="leading-relaxed">
                  Legacy Triangle was built on the belief that every business owner deserves access to capital, expert guidance, and a partner who genuinely cares about their success.
                </p>
                <p className="leading-relaxed">
                  Today, we've helped over 10,000 businesses secure more than $50 million in funding. But we're just getting started.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 fade-up">
              {achievements.map((a) => (
                <div key={a.label} className="bg-[#f8fafc] rounded-xl p-6 text-center border border-gray-100">
                  <div
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-1"
                  >
                    {a.value}
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">
                    {a.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="container">
          <div className="text-center mb-14 fade-up">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Our Values</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              What We Stand For
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-500 max-w-xl mx-auto"
            >
              These core values guide everything we do at Legacy Triangle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className="bg-white rounded-xl p-7 border border-gray-100 hover:shadow-lg transition-shadow fade-up"
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-[#48d480]/20 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#48d480]" />
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-lg mb-2"
                  >
                    {value.title}
                  </h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="text-center mb-14 fade-up">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="text-[#48d480] text-xs font-bold tracking-widest uppercase">Our Team</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-white mb-4"
            >
              The People Behind <span className="text-[#48d480]">Your Success</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/70 max-w-xl mx-auto"
            >
              Our team brings decades of combined experience in business funding, banking, and entrepreneurship.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, idx) => (
              <div
                key={member.name}
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#48d480]/50 transition-all duration-300 hover:-translate-y-2 fade-up"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                {/* Accent bar at top */}
                <div 
                  className="h-1.5 w-full"
                  style={{ backgroundColor: member.color }}
                />
                
                {/* Image container */}
                <div className="relative p-4 pb-0">
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ 
                        imageRendering: 'auto', 
                        WebkitBackfaceVisibility: 'hidden', 
                        backfaceVisibility: 'hidden',
                        objectPosition: member.position === 'top' ? 'center top' : `center ${member.position}`
                      }}
                      loading="eager"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f2d52]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
                
                {/* Info section */}
                <div className="p-5 text-center">
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-white font-bold text-lg mb-1"
                  >
                    {member.name}
                  </h3>
                  <p 
                    style={{ fontFamily: "'Inter', sans-serif", color: member.color }} 
                    className="text-sm font-medium"
                  >
                    {member.role}
                  </p>
                </div>

                {/* Decorative triangle */}
                <div className="absolute bottom-3 right-3 opacity-10 group-hover:opacity-30 transition-opacity">
                  <svg width="40" height="40" viewBox="0 0 100 100">
                    <polygon points="50,10 90,85 10,85" stroke={member.color} strokeWidth="3" fill="none" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-black text-white mb-4"
          >
            Ready to Write Your <span className="text-[#48d480]">Success Story?</span>
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-white/70 mb-8"
          >
            Let's discuss how we can help your business grow with the right funding solution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://app.trulli.ai/marketplace/legacytriangle" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold px-6 py-3 rounded-lg transition-colors">
              Apply Now <ArrowRight size={16} />
            </a>
            <Link to="/contact" className="btn-outline-white flex items-center gap-2">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
